// src/components/LabelledStockForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  FaCheck,
  FaFileExcel,
  FaImages,
  FaList,
  FaPlus,
  FaUndo,
} from "react-icons/fa";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import Select from "react-select";
import {
  defaultDiamondFields,
  defaultStoneFields,
  // labelledStockFields,
  labelledStockTopFields,
} from "../config/fieldsHelper";
import { handleDropDowns } from "../config/dropDownHelpers";
import LabelStockItemForm from "./labelStock/LabelStockItemForm";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import MultiplierStock from "./MultiplierStock";
import {
  useAddLabelledStockMutation,
  useBulkUploadLabelledStockExcelMutation,
  useBulkUploadLabelledStockImagesMutation,
  useUpdateLabelledItemMutation,
} from "../toolkitServices/stockApi";
import { setLabelledList } from "../slices/labelledStockSlice";
import { useNavigate } from "react-router-dom";
import { useGetAllLabelTemplatesQuery } from "../toolkitServices/labelTemplateApi";
import { generateLabelsPdfForStock } from "../utils/labelPdfHelperStock";
import { fillPrnTemplate } from "../utils/fillPrnTemplate";
import { downloadPrnFile } from "../utils/downloadPrnFile";
// 🔹 NEW: barcode + QR libs (LabelPrintOverlay ke liye)
import JsBarcode from "jsbarcode";
import QRCode from "react-qr-code";
import { useGetModuleFieldsQuery } from "../toolkitServices/moduleFieldsApi";
import BulkUpload from "./BulkUpload";
import QuickAddVendorModal from "./other/QuickAddVendorModal";
import VendorAvatarHoverCard from "./other/VendorAvatarHoverCard";

/* ---------- helpers ---------- */
const isBlank = (v) =>
  v === undefined || v === null || (typeof v === "string" && v.trim() === "");

const toUnitString = (u) => {
  const raw = (u && (u.value ?? u)) ?? "";
  const s = String(raw).toLowerCase().trim();
  return s === "gram" || s === "carat" ? s : null;
};

const baseSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "30px",
    height: "30px",
    borderColor: state.isFocused ? "#9481ce" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(148,129,206,.25)" : "none",
  }),
  menu: (b) => ({ ...b, zIndex: 40, fontSize: "12px" }),
  menuList: (base) => ({ ...base, maxHeight: "120px", overflowY: "auto" }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 6px",
    height: "30px",
    overflow: "hidden",
  }),
  indicatorsContainer: (base) => ({ ...base, height: "30px" }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  clearIndicator: (base) => ({ ...base, padding: "4px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
};
const customerSelectStyles = {
  ...baseSelectStyles,
  container: (base) => ({ ...base, width: "100%" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "calc(100% - 28px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  menuList: (base) => ({ ...base, maxHeight: "180px", overflowY: "auto" }),
};

const normalizeStone = (s = {}) => ({
  stnPopupName: s?.stnPopupName || "",
  shape: s?.shape || "",
  size: s?.size || "",
  color: s?.color || "",
  settingType: s?.settingType || "",
  stnPopupWt: s?.stnPopupWt || "",
  stnPopupRateByWt: s?.stnPopupRateByWt || "",
  stnPopupPcs: s?.stnPopupPcs || "",
  stnPopupRateByPcs: s?.stnPopupRateByPcs || "",
  stnPopupAmt: s?.stnPopupAmt || "",
  stnPopupDeduct: s?.stnPopupDeduct || "",
  stnPopupDescription: s?.stnPopupDescription || "",
  unit: toUnitString(s?.unit),
});

/** DIAMONDS: always carat */
const normalizeDiamond = (d = {}) => ({
  diaPopupName: d?.diaPopupName || "",
  diaPopupShape: d?.diaPopupShape || "",
  diaPopupColor: d?.diaPopupColor || "",
  diaPopupClarity: d?.diaPopupClarity || "",
  diaPopupSieve: d?.diaPopupSieve || "",
  diaPopupSize: d?.diaPopupSize || "",
  diaPopupWt: d?.diaPopupWt || "",
  diaPopupRateByWt: d?.diaPopupRateByWt || "",
  diaPopupPcs: d?.diaPopupPcs || "",
  diaPopupRateByPcs: d?.diaPopupRateByPcs || "",
  diaPopupAmt: d?.diaPopupAmt || "",
  diaPopupDeduct: d?.diaPopupDeduct || "deduct",
  diaPopupDescription: d?.diaPopupDescription || "",
  unit: "carat", // fixed for diamonds
});

const stoneRowMeaningful = (s = {}) =>
  !isBlank(s?.stnPopupName) ||
  !isBlank(s?.stnPopupWt) ||
  !isBlank(s?.stnPopupPcs) ||
  !isBlank(s?.stnPopupAmt);

const diamondRowMeaningful = (d = {}) =>
  !isBlank(d?.diaPopupName) ||
  !isBlank(d?.diaPopupWt) ||
  !isBlank(d?.diaPopupPcs) ||
  !isBlank(d?.diaPopupAmt);

const rowLooksFilled = (r = {}) => {
  const keys = [
    "totalWt",
    "packingWt",
    "grossWt",
    "stoneWt",
    "diamondWt",
    "netWt",
    "qty",
    "pcs",
    "stonePcs",
    "diamondPcs",
    "makingPercentage",
    "makingPerGram",
    "fixedMaking",
    "fixedAmount",
    "totalStoneAmount",
    "totalDiamondAmount",
    "hallmarkCharge",
    "mrp",
    "sampleName",
    "huidCode",
    "productCode",
    "rfidCode",
    "description",
  ];
  const anyNum = keys.some((k) => !isBlank(r[k]));
  const anyEntity =
    r?.category?.value ||
    r?.product?.value ||
    r?.design?.value ||
    r?.purity?.value;
  const anyStone =
    Array.isArray(r?.stones) &&
    r.stones.some(
      (s) =>
        !isBlank(s?.stnPopupName) ||
        !isBlank(s?.stnPopupWt) ||
        !isBlank(s?.stnPopupAmt),
    );
  const anyDiamond =
    Array.isArray(r?.diamonds) &&
    r.diamonds.some(
      (d) =>
        !isBlank(d?.diaPopupName) ||
        !isBlank(d?.diaPopupWt) ||
        !isBlank(d?.diaPopupAmt),
    );
  return !!(anyNum || anyEntity || anyStone || anyDiamond);
};

const mapRow = (r = {}) => ({
  totalWt: r.totalWt || "",
  packingWt: r.packingWt || "",
  grossWt: r.grossWt || "",
  stoneWt: r.stoneWt || "",
  diamondWt: r.diamondWt || "", // grams
  netWt: r.netWt || "",

  qty: r.qty || "",
  pcs: r.pcs || "",
  stonePcs: r.stonePcs || "",
  diamondPcs: r.diamondPcs || "",

  makingPercentage: r.makingPercentage || "",
  makingPerGram: r.makingPerGram || "",
  fixedMaking: r.fixedMaking || "",
  fixedAmount: r.fixedAmount || "",

  totalStoneAmount: r.totalStoneAmount || "",
  totalDiamondAmount: r.totalDiamondAmount || "",
  hallmarkCharge: r.hallmarkCharge || "",

  mrp: r.mrp || "",
  sampleName: r.sampleName || "",
  huidCode: r.huidCode || "",
  productCode: r.productCode || "",
  rfidCode: r.rfidCode || "",
  description: r.description || "",

  category: r.category || null,
  product: r.product || null,
  design: r.design || null,
  purity: r.purity || null,

  stones: Array.isArray(r.stones)
    ? r.stones.map(normalizeStone).filter(stoneRowMeaningful)
    : [],

  diamonds: Array.isArray(r.diamonds)
    ? r.diamonds.map(normalizeDiamond).filter(diamondRowMeaningful)
    : [],
});

/* ---------- DB → UI prefill ---------- */
const showIfNonZero = (v) => (v && Number(v) !== 0 ? String(v) : "");

const dbStoneToPopupStone = (s = {}) => ({
  stnPopupName: s?.stoneId ? String(s.stoneId) : "",
  shape: s?.shape ? String(s.shape) : "",
  size: s?.size ? String(s.size) : "",
  color: s?.color ? String(s.color) : "",
  settingType: s?.settingType ? String(s.settingType) : "",
  unit: s?.unit || null,
  stnPopupWt: showIfNonZero(s?.wt),
  stnPopupRateByWt: showIfNonZero(s?.rateByWt),
  stnPopupPcs: showIfNonZero(s?.pcs),
  stnPopupRateByPcs: showIfNonZero(s?.rateByPcs),
  stnPopupAmt: showIfNonZero(s?.amount),
  stnPopupDeduct: s?.deduct || "",
  stnPopupDescription: s?.description || "",
});

const dbDiamondToPopupDiamond = (d = {}) => ({
  diaPopupName: d?.diamondId ? String(d.diamondId) : "",
  diaPopupShape: d?.shape ? String(d.shape) : "",
  diaPopupColor: d?.color ? String(d.color) : "",
  diaPopupClarity: d?.clarity ? String(d.clarity) : "",
  diaPopupSieve: d?.sieve ? String(d.sieve) : "",
  diaPopupSize: d?.size ? String(d.size) : "",
  diaPopupWt: showIfNonZero(d?.wt), // carat
  diaPopupRateByWt: showIfNonZero(d?.rateByWt),
  diaPopupPcs: showIfNonZero(d?.pcs),
  diaPopupRateByPcs: showIfNonZero(d?.rateByPcs),
  diaPopupAmt: showIfNonZero(d?.amount),
  diaPopupDeduct: d?.deduct || "deduct",
  diaPopupDescription: d?.description || "",
  unit: "carat",
});

const ensurePopupStonesShape = (row = {}) => {
  const stones = Array.isArray(row.stones) ? row.stones : [];
  const mapped = stones.map((st) =>
    st && (st.stnPopupName !== undefined || st.stnPopupWt !== undefined)
      ? st
      : dbStoneToPopupStone(st),
  );
  return { ...row, stones: mapped.length ? mapped : [defaultStoneFields] };
};

const ensurePopupDiamondsShape = (row = {}) => {
  const diamonds = Array.isArray(row.diamonds) ? row.diamonds : [];
  const mapped = diamonds.map((d) =>
    d && (d.diaPopupName !== undefined || d.diaPopupWt !== undefined)
      ? d
      : dbDiamondToPopupDiamond(d),
  );
  return { ...row, diamonds: mapped.length ? mapped : [defaultDiamondFields] };
};

const normalizeExistingDataForForm = (data = {}) => {
  const rows = Array.isArray(data.stockEntries) ? data.stockEntries : [data];
  const stockEntries = rows.map((r) =>
    ensurePopupDiamondsShape(ensurePopupStonesShape(r)),
  );
  return { ...data, stockEntries };
};

/* -------- label print helpers ---------- */
const mmToPx = (mm, dpi = 96) => (mm / 25.4) * dpi;

// 🔹 value helper (QR + barcode + text sab ke liye)
const getFieldValue = (item, fieldKey) => {
  if (!fieldKey) return "";
  const raw = item?.[fieldKey];
  return raw === null || raw === undefined ? "" : String(raw);
};

// 🔹 UPDATED: dynamic fields sirf item se value lenge
const resolveElementText = (el, item) => {
  // FIELD: sirf backend ka value, prefix ignore
  if (el.fieldKey) {
    const val = getFieldValue(item, el.fieldKey);
    if (!val) return "";
    return String(val); // only backend value
  }

  // STATIC LABEL: sirf staticText
  return el.staticText || "";
};

/* 🔹 Barcode renderer (print + preview) */
const PrintBarcode = ({ value, left, top, width, height }) => {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current || !value) return;
    try {
      JsBarcode(svgRef.current, value, {
        format: "CODE128",
        displayValue: false,
        height,
        margin: 0,
      });
    } catch (err) {
      console.error("Barcode render error:", err);
    }
  }, [value, height]);

  return (
    <svg
      ref={svgRef}
      style={{
        position: "absolute",
        left,
        top,
        width,
        height,
      }}
    />
  );
};

/* 🔹 AUTO TEMPLATE PICKER: branch + category wise */
const pickTemplateForItem = (templates, item) => {
  if (!Array.isArray(templates) || !templates.length) return null;

  const rawBranch =
    item?.branchId ??
    item?.BranchId ??
    item?.branch?._id ??
    item?.branch?.value ??
    null;

  const rawCategory =
    item?.categoryId ??
    item?.CategoryId ??
    item?.category?._id ??
    item?.category?.value ??
    null;

  const b = rawBranch != null ? String(rawBranch) : null;
  const c = rawCategory != null ? String(rawCategory) : null;

  const inList = (arr, value) =>
    !!value &&
    Array.isArray(arr) &&
    arr.map(String).some((x) => x === String(value));

  let best = null;
  let bestScore = -1;

  for (const t of templates) {
    const allB = !!t.appliesToAllBranches;
    const allC = !!t.appliesToAllCategories;
    const hasB = inList(t.branchIds, b);
    const hasC = inList(t.categoryIds, c);

    const branchMatches = b ? allB || hasB : true;
    const catMatches = c ? allC || hasC : true;

    if (!branchMatches || !catMatches) continue;

    let score = 0;
    if (hasB) score += 2;
    else if (allB) score += 1;
    if (hasC) score += 2;
    else if (allC) score += 1;
    if (t.isDefault) score += 0.5;

    if (score > bestScore) {
      bestScore = score;
      best = t;
    }
  }

  if (best) return best;

  const def = templates.find((t) => t.isDefault);
  return def || templates[0] || null;
};

const printItemsByResolvedTemplate = async (items = [], templates = []) => {
  if (!Array.isArray(items) || !items.length) return;
  if (!Array.isArray(templates) || !templates.length) return;

  const groups = new Map();

  for (const item of items) {
    const template = pickTemplateForItem(templates, item);
    if (!template) continue;

    const key = String(template._id || template.id || "default");

    if (!groups.has(key)) {
      groups.set(key, { template, items: [] });
    }

    groups.get(key).items.push(item);
  }

  if (!groups.size) {
    toast.error("No matching label template found.");
    return;
  }

  for (const { template, items: groupItems } of groups.values()) {
    if (!groupItems.length) continue;

    const templateType = String(template?.templateType || "LabelledItem");

    // ✅ PRN => .prn file download
    if (templateType === "prn") {
      if (!template?.prnCode) {
        toast.error(`PRN code missing in template ${template?.name || ""}`);
        continue;
      }

      const combinedContent = groupItems
        .map((item) =>
          fillPrnTemplate(template.prnCode, {
            ...item,
            ItemCode: item?.itemCode ?? "",
            ProductCode: item?.productCode ?? "",
            ProductName: item?.productName ?? "",
            CategoryName: item?.categoryName ?? "",
            DesignName: item?.designName ?? "",
            PurityName: item?.purityName ?? "",
            GrossWt: item?.grossWt ?? "",
            NetWt: item?.netWt ?? "",
            TotalWt: item?.totalWt ?? "",
            MRP: item?.mrp ?? "",
            RFIDCode: item?.rfidCode ?? "",
            EPCCode: item?.epcCode ?? "",
          }),
        )
        .join("\n");

      downloadPrnFile({
        items: groupItems,
        prnCode: combinedContent,
      });
    } else {
      // ✅ Normal => existing PDF flow
      await generateLabelsPdfForStock(groupItems, template);
    }
  }
};
/* ----------------------------- */

const defaultRow = {
  totalWt: "",
  packingWt: "",
  grossWt: "",
  stoneWt: "",
  diamondWt: "",
  netWt: "",

  qty: "",
  pcs: "",
  stonePcs: "",
  diamondPcs: "",

  makingPercentage: "",
  makingPerGram: "",
  fixedMaking: "",
  fixedAmount: "",

  totalStoneAmount: "",
  totalDiamondAmount: "",
  hallmarkCharge: "",

  mrp: "",
  sampleName: "",
  huidCode: "",
  description: "",

  category: null,
  product: null,
  design: null,
  purity: null,

  stones: [defaultStoneFields],
  diamonds: [defaultDiamondFields],
  images: [],
};

const DEFAULT_VALUES = {
  vendor: "",
  branch: "",
  counter: "",
  stockEntries: [defaultRow],
};

const CARD_HEIGHT_CLASS = "h-[48vh] sm:h-[40vh]";

const num = (v) => {
  if (v === null || v === undefined) return 0;
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt3 = (v) => (Math.round(num(v) * 1000) / 1000).toFixed(3);
const fmt2 = (v) => (Math.round(num(v) * 100) / 100).toFixed(2);

const pickEntity = (obj) =>
  obj && obj.value ? { value: obj.value, label: obj.label || "" } : undefined;

const ZERO_TOTALS = {
  itemsAll: 0,
  itemsValid: 0,
  qty: 0,
  pcs: 0,
  sPcs: 0,
  dPcs: 0,
  tWt: 0,
  pWt: 0,
  sWt: 0,
  dWt: 0,
  gWt: 0,
  nWt: 0,
  sAmt: 0,
  dAmt: 0,
  hallmark: 0,
  mrp: 0,
};

/* ---------- LABEL PRINT OVERLAY (optional preview) ---------- */

const LabelPrintOverlay = ({ template, items, onClose }) => {
  const dpi = template?.dpi || 96;
  const labelWidthMm = template?.widthMm || 50;
  const labelHeightMm = template?.heightMm || 25;

  const labelWidthPx = mmToPx(labelWidthMm, dpi);
  const labelHeightPx = mmToPx(labelHeightMm, dpi);

  // 🔹 PRINT: sirf labels area, page size = label size
  const handlePrint = () => {
    const printRoot = document.getElementById("label-print-root");
    if (!printRoot) {
      window.print();
      return;
    }

    const printContent = printRoot.innerHTML;
    const printWindow = window.open("", "_blank");

    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Labels</title>
          <style>
            @page {
              size: ${labelWidthMm}mm ${labelHeightMm}mm;
              margin: 0;
            }
            * {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              box-sizing: border-box;
            }
            body {
              margin: 0;
              padding: 0;
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header / controls */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">
              Label Print Preview
            </h2>
            <p className="text-[11px] text-slate-500">
              Size:{" "}
              <span className="font-medium">
                {labelWidthMm}mm × {labelHeightMm}mm
              </span>{" "}
              · Labels:{" "}
              <span className="font-medium">{items?.length || 0}</span>
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm"
            >
              Print
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold shadow-sm"
            >
              Close
            </button>
          </div>
        </div>

        {/* Labels (sirf ye area print hoga) */}
        <div className="flex-1 bg-slate-100 px-4 py-3 overflow-auto">
          <div
            id="label-print-root"
            className="grid gap-3 justify-center"
            style={{
              // ek ke niche ek
              gridTemplateColumns: `repeat(1, ${labelWidthPx}px)`,
            }}
          >
            {items.map((item) => (
              <div
                key={item._id}
                className="mx-auto bg-white border border-slate-300 shadow-sm"
                style={{
                  width: labelWidthPx,
                  height: labelHeightPx,
                }}
              >
                <div className="relative w-full h-full">
                  {template.elements?.map((el) => {
                    const left = mmToPx(el.x ?? 0, dpi);
                    const top = mmToPx(el.y ?? 0, dpi);
                    const width = mmToPx(el.w ?? 10, dpi);
                    const height = mmToPx(el.h ?? 5, dpi);

                    // TEXT
                    if (el.type === "text") {
                      const txt = resolveElementText(el, item);
                      if (!txt) return null;

                      const align =
                        el.textAlign === "center"
                          ? "center"
                          : el.textAlign === "right"
                            ? "right"
                            : "left";

                      return (
                        <div
                          key={el.id}
                          style={{
                            position: "absolute",
                            left,
                            top,
                            width,
                            height,
                            fontSize: `${el.fontSize || 9}px`,
                            fontWeight: el.bold ? 600 : 400,
                            textAlign: align,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                              align === "center"
                                ? "center"
                                : align === "right"
                                  ? "flex-end"
                                  : "flex-start",
                            paddingInline: 2,
                          }}
                        >
                          <span className="truncate">{txt}</span>
                        </div>
                      );
                    }

                    // BARCODE
                    if (el.type === "barcode") {
                      const val = getFieldValue(item, el.fieldKey);
                      if (!val) return null;

                      return (
                        <PrintBarcode
                          key={el.id}
                          value={val}
                          left={left}
                          top={top}
                          width={width}
                          height={height}
                        />
                      );
                    }

                    // QR CODE
                    if (el.type === "qrcode") {
                      const val = getFieldValue(item, el.fieldKey);
                      if (!val) return null;

                      return (
                        <div
                          key={el.id}
                          style={{
                            position: "absolute",
                            left,
                            top,
                            width,
                            height,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <QRCode
                            value={val}
                            style={{ width: "100%", height: "100%" }}
                          />
                        </div>
                      );
                    }

                    return null;
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- MAIN FORM ---------- */

const LabelledStockForm = ({
  existingData = null,
  mode = "create",
  itemId = null,
  allowMultipleRows = true,
  autoFocusTopField = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const categories = useSelector((s) => s.categories?.categories);
  const products = useSelector((s) => s.products?.products);
  const vendors = useSelector((s) => s.vendors?.vendors);
  const designs = useSelector((s) => s.designs?.designs);
  const purities = useSelector((s) => s.purities?.purities);
  const branches = useSelector((s) => s.branches?.branches);
  const counters = useSelector((s) => s.counters?.counters);

  const [isMultiplierOpen, setIsMultiplierOpen] = useState(false);
  const [printContext, setPrintContext] = useState(null);
  const [bulkModal, setBulkModal] = useState(null);
  const [imagesTouched, setImagesTouched] = useState(false);
  const [quickVendorOpen, setQuickVendorOpen] = useState(false);

  // mount gate
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    shouldUnregister: true,
    defaultValues: existingData
      ? normalizeExistingDataForForm(existingData)
      : DEFAULT_VALUES,
  });

  const authUser = useSelector((s) => s.auth?.user);
  const tenantId = authUser?.tenantId;
  const selectedVendor = watch("vendor");
  const branch = watch("branch");
  const { data: { data: lblStkFields = [] } = {}, isLoading } =
    useGetModuleFieldsQuery("LABELLED_STOCK");
  const labelledStockFields = useMemo(
    () => lblStkFields.filter((f) => f.visibility !== false),
    [lblStkFields],
  );
  const { data: labelTemplatesRes } = useGetAllLabelTemplatesQuery(
    {},
    { skip: authUser?.tenantId ? false : true },
  );
  const labelTemplates =
    labelTemplatesRes?.data || labelTemplatesRes?.items || [];

  useEffect(() => {
    let bid = authUser?.employeeDetails?.branch?._id;
    let cid = authUser?.employeeDetails?.counter?._id;
    let bname = authUser?.employeeDetails?.branch?.branchName;
    let cname = authUser?.employeeDetails?.counter?.counterName;
    if (mode != "edit" && authUser?.role == "staff" && bid) {
      setValue("branch", { value: bid, label: bname });
    }
    if (mode != "edit" && authUser?.role == "staff" && cid) {
      setValue("counter", { value: cid, label: cname });
    }
  }, [authUser, mode]);

  useEffect(() => {
    if (mode === "edit" && existingData) {
      const normalized = normalizeExistingDataForForm(existingData);
      reset(normalized);
      (normalized.stockEntries || []).forEach((r, i) => {
        setValue(`stockEntries.${i}.stones`, r?.stones ?? [], {
          shouldDirty: false,
          shouldValidate: false,
        });
        setValue(`stockEntries.${i}.diamonds`, r?.diamonds ?? [], {
          shouldDirty: false,
          shouldValidate: false,
        });
      });
    }
  }, [mode, existingData, reset, setValue]);

  const [addLabelledStock, addState] = useAddLabelledStockMutation();
  const [updateLabelledItem, updateState] = useUpdateLabelledItemMutation();
  const [bulkUploadExcel, { isLoading: excelLoading }] =
    useBulkUploadLabelledStockExcelMutation();

  const [bulkUploadImages, { isLoading: zipLoading }] =
    useBulkUploadLabelledStockImagesMutation();

  const isSaving = addState.isLoading || updateState.isLoading;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "stockEntries",
  });

  useEffect(() => {
    if (mode === "edit") {
      const rows = getValues("stockEntries") || [];
      if (rows.length > 1) replace(rows.slice(0, 1));
    }
  }, [mode, replace, getValues]);

  const firstFieldRef = useRef(null);
  const listRef = useRef(null);
  const rowFirstRefs = useRef({});

  // refs for top fields
  const topFieldRefs = useRef({}); // name -> element or react-select instance

  // deep watch rows for summary
  const entriesWatch = useWatch({ control, name: "stockEntries" }) || [];

  const totals = useMemo(() => {
    const rows = (Array.isArray(entriesWatch) ? entriesWatch : []).filter(
      Boolean,
    );
    if (!rows.length) return ZERO_TOTALS;

    const validRows = rows.filter((r) => rowLooksFilled(r || {}));

    let qty = 0,
      pcs = 0,
      sPcs = 0,
      dPcs = 0;
    let tWt = 0,
      pWt = 0,
      sWt = 0,
      dWt = 0,
      gWt = 0,
      nWt = 0;
    let sAmt = 0,
      dAmt = 0,
      hallmark = 0,
      mrp = 0;

    const toNum = (v) => (v === "" || v == null ? 0 : Number(v) || 0);

    for (const raw of rows) {
      const r = raw || {};
      const stones = Array.isArray(r.stones) ? r.stones : [];
      const diamonds = Array.isArray(r.diamonds) ? r.diamonds : [];

      const rowStonePcs = stones.reduce(
        (acc, s) => acc + toNum(s?.stnPopupPcs),
        0,
      );
      const rowStoneAmt = stones.reduce(
        (acc, s) => acc + toNum(s?.stnPopupAmt),
        0,
      );
      // stones: gram or carat → convert to grams if deduct
      const rowStoneWt = stones
        .filter(
          (s) => String(s?.stnPopupDeduct || "").toLowerCase() === "deduct",
        )
        .reduce((acc, s) => {
          const wt = toNum(s?.stnPopupWt);
          const unit = (s?.unit?.value || s?.unit || "").toLowerCase();
          return acc + (unit === "carat" ? wt / 5 : wt);
        }, 0);

      const rowDiamondPcs = diamonds.reduce(
        (acc, d) => acc + toNum(d?.diaPopupPcs),
        0,
      );
      const rowDiamondAmt = diamonds.reduce(
        (acc, d) => acc + toNum(d?.diaPopupAmt),
        0,
      );
      // diamonds: ALWAYS carat → convert to grams if deduct
      const rowDiamondWt = diamonds
        .filter(
          (d) => String(d?.diaPopupDeduct || "").toLowerCase() === "deduct",
        )
        .reduce((acc, d) => acc + toNum(d?.diaPopupWt) / 5, 0);

      const T = toNum(r.totalWt);
      const P = toNum(r.packingWt);
      const S = toNum(r.stoneWt) || rowStoneWt;
      const D = rowDiamondWt || toNum(r.diamondWt) / 5; // grams
      const G = T - P;
      const N = T - P - S - D;

      qty += toNum(r.qty);
      pcs += toNum(r.pcs);
      sPcs += rowStonePcs;
      dPcs += rowDiamondPcs || toNum(r.diamondPcs);

      tWt += T;
      pWt += P;
      gWt += G;
      sWt += S;
      dWt += D;
      nWt += N;

      sAmt += rowStoneAmt;
      dAmt += rowDiamondAmt || toNum(r.totalDiamondAmount);
      hallmark += toNum(r.hallmarkCharge);
      mrp += toNum(r.mrp);
    }

    return {
      itemsAll: rows.length,
      itemsValid: validRows.length,
      qty,
      pcs,
      sPcs,
      dPcs,
      tWt,
      pWt,
      sWt,
      dWt,
      gWt,
      nWt,
      sAmt,
      dAmt,
      hallmark,
      mrp,
    };
  }, [entriesWatch]);

  const focusRowAt = (idx) => {
    const cards = listRef.current?.querySelectorAll(".stock-card");
    if (!cards || !cards.length) return;
    const i = Math.max(0, Math.min(idx, cards.length - 1));
    const card = cards[i];
    card?.scrollIntoView({ behavior: "smooth", block: "start" });
    const id = card?.dataset?.fieldId;
    const el = rowFirstRefs.current[id];
    if (el?.focus) el.focus();
  };

  // auto-focus first top field
  const focusFirstTopField = () => {
    const vendorEl = topFieldRefs.current?.vendor;
    if (vendorEl && typeof vendorEl.focus === "function") {
      vendorEl.focus();
      return;
    }
    const firstKey = Object.keys(topFieldRefs.current || {})[0];
    const el = firstKey ? topFieldRefs.current[firstKey] : null;
    if (el && typeof el.focus === "function") el.focus();
  };

  useEffect(() => {
    if (!autoFocusTopField) return;
    const raf = requestAnimationFrame(() => focusFirstTopField());
    return () => cancelAnimationFrame(raf);
  }, [autoFocusTopField, mounted]);

  useEffect(() => {
    if (!autoFocusTopField) return;
    const hasAnyOptions =
      (Array.isArray(vendors) && vendors.length) ||
      (Array.isArray(branches) && branches.length) ||
      (Array.isArray(counters) && counters.length);
    if (hasAnyOptions) {
      const id = requestAnimationFrame(() => focusFirstTopField());
      return () => cancelAnimationFrame(id);
    }
  }, [vendors, branches, counters, autoFocusTopField]);

  // 🔹 SUCCESS effect – yahi se auto label print hoga (branch+category wise)
  useEffect(() => {
    if (!addState.isSuccess) return;

    const created = addState?.data?.data || addState?.data?.createdItems || [];

    toast.success(
      created.length
        ? `Labelled stock created (${created.length})`
        : "Labelled stock created",
    );

    if (Array.isArray(created) && created.length) {
      const tiny = created.map((d) => ({
        _id: d._id,
        itemCode: d.itemCode,
        branchId: d.branchId,
        counterId: d.counterId,
        categoryId: d.categoryId ?? d.CategoryId ?? null,
      }));
      dispatch(setLabelledList(tiny));
    }

    (async () => {
      try {
        const createdItems = Array.isArray(created) ? created : [];
        if (!createdItems.length) return;
        if (!labelTemplates.length) return;

        await printItemsByResolvedTemplate(createdItems, labelTemplates);
      } catch (err) {
        console.error("Error generating label/PRN after create:", err);
      }
    })();

    // form reset + focus
    reset(DEFAULT_VALUES);
    requestAnimationFrame(() =>
      setTimeout(() => firstFieldRef.current?.focus(), 0),
    );
    requestAnimationFrame(() => focusRowAt(0));
    requestAnimationFrame(() => focusFirstTopField());
  }, [addState.isSuccess, addState.data, dispatch, reset, labelTemplates]);

  useEffect(() => {
    if (addState.error) {
      const msg =
        addState.error?.data?.msg ||
        addState.error?.error ||
        "Error creating stock";
      toast.error(msg);
    }
  }, [addState.error]);

  // 🔹 EDIT SUCCESS → auto label print (same as create)
  useEffect(() => {
    if (!updateState.isSuccess) return;

    toast.success("Item updated");

    (async () => {
      try {
        const updatedRaw =
          updateState?.data?.data ||
          updateState?.data?.item ||
          updateState?.data?.updatedItem ||
          updateState?.data ||
          null;

        if (!updatedRaw || !labelTemplates.length) return;

        const updatedArray = Array.isArray(updatedRaw)
          ? updatedRaw
          : [updatedRaw];

        if (!updatedArray.length) return;

        await printItemsByResolvedTemplate(updatedArray, labelTemplates);
      } catch (err) {
        console.error("Error generating label/PRN after update:", err);
      }
    })();
  }, [updateState.isSuccess, updateState.data, labelTemplates]);

  useEffect(() => {
    if (updateState.error) {
      const msg =
        updateState.error?.data?.msg ||
        updateState.error?.error ||
        "Error updating item";
      toast.error(msg);
    }
  }, [updateState.error]);

  useEffect(() => {
    const onKeys = (e) => {
      if (mode !== "edit" && e.ctrlKey && e.key === "+") {
        e.preventDefault();
        handleAddRow();
      }
      if (e.ctrlKey && e.key.toLowerCase() === "r") {
        e.preventDefault();
        if (mode === "edit" && existingData) {
          const normalized = normalizeExistingDataForForm(existingData);
          reset(normalized);
          (normalized.stockEntries || []).forEach((r, i) => {
            setValue(`stockEntries.${i}.stones`, r?.stones ?? [], {
              shouldDirty: false,
              shouldValidate: false,
            });
            setValue(`stockEntries.${i}.diamonds`, r?.diamonds ?? [], {
              shouldDirty: false,
              shouldValidate: false,
            });
          });
        } else {
          reset(DEFAULT_VALUES);
          requestAnimationFrame(() => {
            focusRowAt(0);
            setTimeout(() => firstFieldRef.current?.focus(), 0);
          });
          requestAnimationFrame(() => focusFirstTopField());
        }
      }
      if (mode !== "edit" && e.ctrlKey && e.key === "*") {
        e.preventDefault();
        setIsMultiplierOpen(true);
      }
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    window.addEventListener("keydown", onKeys);
    return () => window.removeEventListener("keydown", onKeys);
  }, [handleSubmit, mode, existingData, reset, setValue]);

  const duplicateFirstEntry = (n = 0) => {
    if (mode === "edit") return;
    const entries = getValues("stockEntries") || [];
    if (!entries.length || n < 1) return;
    const first = entries[0];
    const clone = () =>
      typeof structuredClone === "function"
        ? structuredClone(first)
        : JSON.parse(JSON.stringify(first));
    const clones = Array.from({ length: n }, clone).map((c) => {
      delete c.id;
      delete c.itemCode;
      return c;
    });
    append(clones, { shouldFocus: true });
    requestAnimationFrame(() =>
      setTimeout(() => focusRowAt(entries.length - 1 + n), 0),
    );
  };

  const handleAddRow = () => {
    if (mode === "edit" || !allowMultipleRows) return;
    append({ ...defaultRow });
    requestAnimationFrame(() =>
      setTimeout(() => {
        const nextIdx = (getValues("stockEntries")?.length || 1) - 1;
        focusRowAt(nextIdx);
      }, 0),
    );
  };

  /* ---------- SUBMIT ---------- */
  const onSubmit = async (form) => {
    const vendor = form.vendor || null;
    const branch = form.branch || null;
    const counter = form.counter || null;
    const rows = Array.isArray(form.stockEntries) ? form.stockEntries : [];

    // 🔹 EDIT MODE
    if (mode === "edit") {
      if (!itemId) return toast.error("Missing item id");
      if (!rows.length) return toast.error("Nothing to update");
      const r = rows[0];

      const patch = {
        totalWt: r.totalWt,
        packingWt: r.packingWt,
        grossWt: r.grossWt,
        stoneWt: r.stoneWt,
        diamondWt: r.diamondWt, // grams (auto or manual)
        netWt: r.netWt,
        qty: r.qty,
        pcs: r.pcs,
        stonePcs: r.stonePcs,
        diamondPcs: r.diamondPcs,
        makingPercentage: r.makingPercentage,
        makingPerGram: r.makingPerGram,
        fixedMaking: r.fixedMaking,
        fixedAmount: r.fixedAmount,
        totalStoneAmount: r.totalStoneAmount,
        totalDiamondAmount: r.totalDiamondAmount,
        hallmarkCharge: r.hallmarkCharge,
        mrp: r.mrp,
        sampleName: r.sampleName,
        huidCode: r.huidCode,
        productCode: r.productCode,
        rfidCode: r.rfidCode,
        description: r.description,
        stones: r.stones || [],
        diamonds: r.diamonds || [],
      };

      const entities = {};
      const v = pickEntity(vendor);
      if (v) entities.vendor = v;
      const b = pickEntity(branch);
      if (b) entities.branch = b;
      const co = pickEntity(counter);
      if (co) entities.counter = co;
      const c = pickEntity(r.category);
      if (c) entities.category = c;
      const p = pickEntity(r.product);
      if (p) entities.product = p;
      const d = pickEntity(r.design);
      if (d) entities.design = d;
      const pu = pickEntity(r.purity);
      if (pu) entities.purity = pu;
      if (Object.keys(entities).length) patch.entities = entities;

      const formData = new FormData();
      formData.append("id", itemId);
      formData.append("data", JSON.stringify(patch));

      // ✅ new: ALWAYS send kept existing images (url is enough)
      const existingImages = (r.images || [])
        .filter((img) => !img.file && img.url)
        .map((img) => ({
          url: img.url,
          publicId: img.publicId || null, // optional
        }));

      formData.append("imagesMeta", JSON.stringify(existingImages));

      // ✅ images append (EDIT CASE FIX)
      (r.images || []).forEach((img) => {
        if (img?.file instanceof File) {
          formData.append("images", img.file);
        }
      });
      try {
        await updateLabelledItem(formData).unwrap();
      } catch {}
      return;
    }

    // 🔹 CREATE MODE + FORM DATA

    if (!vendor?.value) return toast.error("Vendor is required");
    if (!branch?.value) return toast.error("Branch is required");
    if (!counter?.value) return toast.error("Counter is required");

    const validRows = rows.filter(rowLooksFilled);
    if (!validRows.length)
      return toast.error("Add at least one valid stock row");

    const stockEntries = validRows.map(mapRow);

    const payload = {
      vendor,
      branch,
      counter,
      stockEntries,
    };

    const formData = new FormData();
    formData.append("vendor", JSON.stringify(payload.vendor));
    formData.append("branch", JSON.stringify(payload.branch));
    formData.append("counter", JSON.stringify(payload.counter));
    formData.append("stockEntries", JSON.stringify(payload.stockEntries));

    // Images
    validRows.forEach((row, i) => {
      const images = row.images || [];
      images.forEach((img) => {
        if (img?.file instanceof File) {
          formData.append(`stockEntries[${i}].images`, img.file);
        }
      });
    });
    // return
    try {
      await addLabelledStock(formData).unwrap();
    } catch {}
  };

  const handleFocusCapture = (e) => {
    const card = e.target.closest(".stock-card");
    if (card) card.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const saveLabel = mode === "edit" ? "Update" : "Save";

  // ensure Counter exists
  const topFields = useMemo(() => {
    const hasCounter = (labelledStockTopFields || []).some(
      (f) => f?.name === "counter",
    );
    if (hasCounter) return labelledStockTopFields;
    return [
      ...(labelledStockTopFields || []),
      {
        name: "counter",
        label: "Counter",
        type: "dropdown",
        mandatory: true,
        visibility: true,
      },
    ];
  }, []);

  const openBulkModal = (type) => {
    const vendor = getValues("vendor");
    const branch = getValues("branch");
    const counter = getValues("counter");

    if (!vendor?.value || !branch?.value || !counter?.value) {
      toast.error("Please select Vendor, Branch and Counter first");
      return;
    }

    setBulkModal(type);
  };

  const showBulkErrors = (err) => {
    const data = err?.data;

    // fallback
    if (!data) {
      toast.error("Upload failed");
      return;
    }

    // simple message
    if (data.msg) {
      toast.error(data.msg);
    }

    // 🔥 row-wise errors
    if (Array.isArray(data.errors)) {
      data.errors.slice(0, 5).forEach((e) => {
        toast.error(`Row ${e.row} • ${e.field}: ${e.error}`);
      });

      if (data.errors.length > 5) {
        toast.error(`+${data.errors.length - 5} more errors`);
      }
    }
  };

  return (
    <>
      {/* PRINT OVERLAY (optional) */}
      {printContext && (
        <LabelPrintOverlay
          template={printContext.template}
          items={printContext.items}
          onClose={() => setPrintContext(null)}
        />
      )}

      {isMultiplierOpen && mode !== "edit" && (
        <MultiplierStock
          open={isMultiplierOpen}
          setOpen={setIsMultiplierOpen}
          onApply={duplicateFirstEntry}
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        {/* Top inputs */}
        <div className="bg-white shadow-sm rounded-xl px-3 py-2 border">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 ">
            {topFields?.map((item, idx) => (
              <div
                key={idx}
                className={`min-w-0 ${item.name === "vendor" ? "sm:col-span-2" : ""}`}
              >
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  {item?.label}{" "}
                  {item?.mandatory && <span className="text-red-500">*</span>}
                </label>

                {item?.type === "text" && (
                  <>
                    <input
                      ref={(el) => {
                        if (!topFieldRefs.current[item.name])
                          topFieldRefs.current[item.name] = el;
                      }}
                      {...register(item.name, {
                        required: item?.mandatory
                          ? `${item.label} is required`
                          : false,
                      })}
                      placeholder={`Enter ${item.label}`}
                      className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                    {errors[item.name] && (
                      <p className="text-red-500 text-[11px] mt-1">
                        {errors[item.name].message}
                      </p>
                    )}
                  </>
                )}

                {item?.type === "number" && (
                  <>
                    <input
                      ref={(el) => {
                        if (!topFieldRefs.current[item.name])
                          topFieldRefs.current[item.name] = el;
                      }}
                      type="number"
                      {...register(item.name, {
                        required: item?.mandatory
                          ? `${item.label} is required`
                          : false,
                      })}
                      placeholder={`Enter ${item.label}`}
                      className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                    {errors[item.name] && (
                      <p className="text-red-500 text-[11px] mt-1">
                        {errors[item.name].message}
                      </p>
                    )}
                  </>
                )}

                {item?.type === "date" && (
                  <>
                    <input
                      ref={(el) => {
                        if (!topFieldRefs.current[item.name])
                          topFieldRefs.current[item.name] = el;
                      }}
                      type="date"
                      {...register(item.name, {
                        required: item?.mandatory
                          ? `${item.label} is required`
                          : false,
                      })}
                      className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                    {errors[item.name] && (
                      <p className="text-red-500 text-[11px] mt-1">
                        {errors[item.name].message}
                      </p>
                    )}
                  </>
                )}

                {item?.type === "dropdown" && (
                  <>
                    <Controller
                      name={item.name}
                      control={control}
                      rules={{
                        required: item?.mandatory
                          ? `${item.label} is required`
                          : false,
                      }}
                      render={({ field }) => (
                        <div className={item.name === "vendor" ? "flex items-center gap-2" : ""}>
                          <div className="min-w-0 flex-1">
                            <Select
                              {...field}
                              ref={(el) => {
                                if (el && !topFieldRefs.current[item.name]) {
                                  topFieldRefs.current[item.name] = el;
                                }
                              }}
                              options={handleDropDowns({
                                key: item["name"],
                                vendors,
                                categories,
                                products,
                                designs,
                                purities,
                                branches,
                                counters,
                                category: "",
                                product: "",
                                branch,
                              })}
                              styles={customerSelectStyles}
                              classNamePrefix="react-select"
                              placeholder={`Select...`}
                              isClearable
                              menuPlacement="auto"
                            />
                          </div>
                          {item.name === "vendor" && mode !== "edit" && (
                            <button
                              type="button"
                              onClick={() => setQuickVendorOpen(true)}
                              className="h-[30px] shrink-0 rounded-md border px-3 text-sm font-semibold text-[#6b59c6] hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-[#9481ce]"
                              title="Quick add vendor"
                              aria-label="Quick add vendor"
                            >
                              +
                            </button>
                          )}
                          {item.name === "vendor" && (
                            <VendorAvatarHoverCard vendor={selectedVendor} />
                          )}
                        </div>
                      )}
                    />
                    {errors[item.name] && (
                      <p className="text-red-500 text-[11px] mt-1">
                        {errors[item.name].message}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}

            {/* 🔹 BULK UPLOAD BUTTONS */}
            {/* 🔹 BULK UPLOAD ICON BUTTONS */}
            <div className="flex justify-end items-end gap-2 mt-2 lg:col-span-3">
              <motion.button
                type="button"
                onClick={handleAddRow}
                whileHover={{ y: -2, scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                title="Add item"
                className="md:hidden group relative inline-flex items-center justify-center h-10 w-10 rounded-xl
             bg-white/90 backdrop-blur
             border border-slate-200 text-slate-700
             shadow-[0_6px_18px_rgba(15,23,42,0.08)]
             hover:shadow-[0_10px_30px_rgba(16,185,129,0.18)]
             transition-all duration-200
             focus:outline-none focus:ring-2 focus:ring-emerald-300"
              >
                {/* glow ring */}
                <span
                  className="pointer-events-none absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
               ring-2 ring-emerald-200/70 transition-opacity"
                />
                {/* soft gradient wash */}
                <span
                  className="pointer-events-none absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
               bg-gradient-to-br from-emerald-50 via-white to-emerald-100/60 transition-opacity"
                />
                <FaPlus className="relative text-[16px] group-hover:scale-110 transition-transform duration-200" />
              </motion.button>
              <motion.button
                type="button"
                onClick={() => openBulkModal("excel")}
                whileHover={{ y: -2, scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                title="Upload Bulk Excel"
                className="group relative inline-flex items-center justify-center h-10 w-10 rounded-xl
             bg-white/90 backdrop-blur
             border border-slate-200 text-slate-700
             shadow-[0_6px_18px_rgba(15,23,42,0.08)]
             hover:shadow-[0_10px_30px_rgba(16,185,129,0.18)]
             transition-all duration-200
             focus:outline-none focus:ring-2 focus:ring-emerald-300"
              >
                {/* glow ring */}
                <span
                  className="pointer-events-none absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
               ring-2 ring-emerald-200/70 transition-opacity"
                />
                {/* soft gradient wash */}
                <span
                  className="pointer-events-none absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
               bg-gradient-to-br from-emerald-50 via-white to-emerald-100/60 transition-opacity"
                />
                <FaFileExcel className="relative text-[16px] group-hover:scale-110 transition-transform duration-200" />
              </motion.button>

              <motion.button
                type="button"
                onClick={() => openBulkModal("images")}
                whileHover={{ y: -2, scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                title="Upload Bulk Images"
                className="group relative inline-flex items-center justify-center h-10 w-10 rounded-xl
             bg-white/90 backdrop-blur
             border border-slate-200 text-slate-700
             shadow-[0_6px_18px_rgba(15,23,42,0.08)]
             hover:shadow-[0_10px_30px_rgba(99,102,241,0.20)]
             transition-all duration-200
             focus:outline-none focus:ring-2 focus:ring-indigo-300"
              >
                <span
                  className="pointer-events-none absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
               ring-2 ring-indigo-200/70 transition-opacity"
                />
                <span
                  className="pointer-events-none absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
               bg-gradient-to-br from-indigo-50 via-white to-indigo-100/60 transition-opacity"
                />
                <FaImages className="relative text-[16px] group-hover:scale-110 transition-transform duration-200" />
              </motion.button>
            </div>
          </div>
        </div>

        {/* Rows viewer */}
        <div className="bg-white shadow-sm rounded-xl p-0  z-10 ">
          <div
            ref={listRef}
            onFocusCapture={handleFocusCapture}
            className={[
              `relative ${CARD_HEIGHT_CLASS} min-h-[40vh] overflow-y-auto scroll-smooth snap-y snap-mandatory p-0 `,
              mounted
                ? "visible opacity-100"
                : "invisible opacity-0 pointer-events-none",
            ].join(" ")}
            style={{ scrollbarGutter: "stable both-edges" }}
          >
            {fields?.map((item, rowindex) => (
              <LabelStockItemForm
                key={item.id}
                fieldId={item.id}
                control={control}
                watch={watch}
                getValues={getValues}
                labelledStockFields={labelledStockFields}
                item={item}
                rowindex={rowindex}
                register={register}
                errors={errors}
                setValue={setValue}
                firstFieldRef={rowindex === 0 ? firstFieldRef : undefined}
                registerFirstField={(id, el) => {
                  if (el) rowFirstRefs.current[id] = el;
                }}
                cardHeightClass={CARD_HEIGHT_CLASS}
                showDelete={mode !== "edit" && allowMultipleRows}
                onDelete={
                  mode !== "edit" && allowMultipleRows
                    ? () => {
                        const targetIdx = Math.max(0, rowindex - 1);
                        remove(rowindex);
                        requestAnimationFrame(() =>
                          setTimeout(() => focusRowAt(targetIdx), 0),
                        );
                      }
                    : undefined
                }
                mode={mode}
                setImagesTouched={setImagesTouched}
              />
            ))}
          </div>

          {/* ---- SUMMARY BAR ---- */}
          {mode !== "edit" && (
            <div className="border rounded-lg bg-[#f3e8c8] px-3 py-2 mt-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[12px] font-semibold text-slate-600">
                  Summary
                </span>
                <span className="text-[11px] text-slate-500">
                  Items: <b>{totals.itemsValid}</b> / {totals.itemsAll}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                <SummaryChip label="Qty" value={totals.qty} />
                <SummaryChip label="Pcs" value={totals.pcs} />
                <SummaryChip label="Stone Pcs" value={totals.sPcs} />
                <SummaryChip label="Diamond Pcs" value={totals.dPcs} />

                <SummaryChip label="Total Wt" value={fmt3(totals.tWt)} />
                <SummaryChip label="Packing Wt" value={fmt3(totals.pWt)} />
                <SummaryChip label="Gross Wt" value={fmt3(totals.gWt)} />
                <SummaryChip label="Stone Wt" value={fmt3(totals.sWt)} />
                <SummaryChip label="Diamond Wt" value={fmt3(totals.dWt)} />
                <SummaryChip label="Net Wt" value={fmt3(totals.nWt)} />

                <SummaryChip
                  label="Stone Amt"
                  value={`₹${fmt2(totals.sAmt)}`}
                />
                <SummaryChip
                  label="Diamond Amt"
                  value={`₹${fmt2(totals.dAmt)}`}
                />
                <SummaryChip
                  label="Hallmark"
                  value={`₹${fmt2(totals.hallmark)}`}
                />
                <SummaryChip label="MRP" value={`₹${fmt2(totals.mrp)}`} />
              </div>
            </div>
          )}

          {/* ---- ACTIONS ---- */}
          <div className="pt-2 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-between sm:items-center">
            {/* LEFT: Add Row */}
            {mode !== "edit" && allowMultipleRows && (
              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98, y: 0 }}
                type="button"
                onClick={handleAddRow}
                className="group inline-flex w-full sm:w-auto justify-center items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3.5 py-2 text-[11px] font-medium text-sky-700 shadow-sm hover:bg-sky-100 hover:border-sky-300 hover:shadow-md transition-all"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-sky-500 text-white">
                  <FaPlus className="text-[10px]" />
                </span>
                <span className="tracking-wide">Add Row</span>

                <span className="ml-1 h-4 w-px bg-sky-200 group-hover:bg-sky-300" />

                <span className="inline-flex items-center rounded-full bg-white/80 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide text-sky-700">
                  Ctrl + +
                </span>
              </motion.button>
            )}

            {/* RIGHT: Save / Reset / List */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full sm:w-auto sm:ml-auto">
              {/* Save */}
              <motion.button
                whileHover={{
                  scale: isSaving ? 1 : 1.02,
                  y: isSaving ? 0 : -1,
                }}
                whileTap={{ scale: isSaving ? 1 : 0.98, y: 0 }}
                type="submit"
                disabled={isSaving}
                className="group inline-flex w-full sm:w-auto justify-center items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-[11px] font-semibold text-white shadow-sm hover:bg-emerald-600 hover:shadow-md transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <FaCheck className="text-[10px]" />
                </span>
                <span className="tracking-wide">
                  {isSaving
                    ? mode === "edit"
                      ? "Updating..."
                      : "Saving..."
                    : saveLabel}
                </span>

                <span className="ml-1 h-4 w-px bg-emerald-300/80 group-hover:bg-white/60" />

                <span className="inline-flex items-center rounded-full bg-emerald-400/90 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide">
                  Ctrl + S
                </span>
              </motion.button>

              {/* Reset */}
              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98, y: 0 }}
                type="button"
                onClick={() => {
                  if (mode === "edit" && existingData) {
                    const normalized =
                      normalizeExistingDataForForm(existingData);
                    reset(normalized);

                    (normalized.stockEntries || []).forEach((r, i) => {
                      setValue(`stockEntries.${i}.stones`, r?.stones ?? [], {
                        shouldDirty: false,
                        shouldValidate: false,
                      });
                      setValue(
                        `stockEntries.${i}.diamonds`,
                        r?.diamonds ?? [],
                        {
                          shouldDirty: false,
                          shouldValidate: false,
                        },
                      );
                    });
                  } else {
                    reset(DEFAULT_VALUES);
                    requestAnimationFrame(() => {
                      focusRowAt(0);
                      setTimeout(() => firstFieldRef.current?.focus(), 0);
                      focusFirstTopField();
                    });
                  }
                }}
                className="group inline-flex w-full sm:w-auto justify-center items-center gap-2 rounded-full border border-slate-300 bg-slate-50 px-3.5 py-2 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-100 hover:border-slate-400 hover:shadow-md transition-all"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white">
                  <FaUndo className="text-[10px]" />
                </span>
                <span className="tracking-wide">Reset</span>

                <span className="ml-1 h-4 w-px bg-slate-200 group-hover:bg-slate-300" />

                <span className="inline-flex items-center rounded-full bg-white/80 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide text-slate-700">
                  Ctrl + R
                </span>
              </motion.button>

              {/* List */}
              <motion.button
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98, y: 0 }}
                type="button"
                onClick={() => navigate("/stocks/labelled-stock-list")}
                className="group inline-flex w-full sm:w-auto justify-center items-center gap-2 rounded-full bg-indigo-500 px-3.5 py-2 text-[11px] font-semibold text-white shadow-sm hover:bg-indigo-600 hover:shadow-md transition-all"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <FaList className="text-[10px]" />
                </span>
                <span className="tracking-wide">Labelled Stock List</span>

                <span className="ml-1 h-4 w-px bg-indigo-300/80 group-hover:bg-white/60" />

                <span className="inline-flex items-center rounded-full bg-indigo-400/90 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide">
                  Ctrl + ⌫
                </span>
              </motion.button>
            </div>
          </div>
        </div>
      </form>

      {bulkModal && (
        <BulkUpload
          excelLoading={excelLoading}
          zipLoading={zipLoading}
          bulkModal={bulkModal}
          setBulkModal={setBulkModal}
          bulkUploadExcel={bulkUploadExcel}
          getValues={getValues}
          bulkUploadImages={bulkUploadImages}
          showBulkErrors={showBulkErrors}
        />
      )}
      <QuickAddVendorModal
        open={quickVendorOpen}
        onClose={() => setQuickVendorOpen(false)}
        onAdded={(created) => {
          const option = (handleDropDowns({ key: "vendor", vendors: [created] }) || [])[0];
          if (option) {
            setValue("vendor", option, { shouldDirty: true, shouldValidate: true });
          }
          requestAnimationFrame(() => {
            setTimeout(() => topFieldRefs.current?.vendor?.focus?.(), 80);
          });
        }}
      />
    </>
  );
};

const SummaryChip = ({ label, value }) => (
  <div className="rounded-lg border bg-white px-3 py-2 shadow-sm">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="text-sm font-semibold text-slate-800">{value}</div>
  </div>
);

export default LabelledStockForm;
